import { useState, useEffect, useCallback } from 'react';
import { Bell, Volume2, VolumeX, Mail, Megaphone, AlertTriangle, Star, FileText, Users, Zap, MessageSquare, Check, Info, Send, Loader2, BarChart3, Trash2, RefreshCw, Plus, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, BarChart, Bar } from 'recharts';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const notificationTypes = [
  { key: 'notes', label: 'QA Notes', icon: FileText, description: 'When notes are created or updated on calls' },
  { key: 'scores', label: 'Call Scores', icon: Star, description: 'When calls receive new scores or score changes' },
  { key: 'coaching', label: 'Coaching Plans', icon: Zap, description: 'Coaching plan assignments and updates' },
  { key: 'reviews', label: 'Review Requests', icon: Check, description: 'New review requests and status changes' },
  { key: 'users', label: 'User Management', icon: Users, description: 'New users, role changes, team assignments' },
  { key: 'alerts', label: 'Alerts', icon: AlertTriangle, description: 'Compliance alerts and system warnings' },
  { key: 'tickets', label: 'Support Tickets', icon: MessageSquare, description: 'Ticket updates and responses' },
  { key: 'chat', label: 'Chat Messages', icon: MessageSquare, description: 'Direct messages and team chats' },
  { key: 'announcements', label: 'Announcements', icon: Megaphone, description: 'Admin announcements and broadcasts' },
  { key: 'tasks', label: 'Tasks', icon: Check, description: 'Task assignments and completions' },
  { key: 'system', label: 'System Updates', icon: Info, description: 'System maintenance and updates' },
];

const priorityColors = {
  urgent: 'bg-red-100 text-red-800 border-red-200',
  high: 'bg-orange-100 text-orange-800 border-orange-200',
  normal: 'bg-blue-100 text-blue-800 border-blue-200',
  low: 'bg-slate-100 text-slate-800 border-slate-200',
};

export default function NotificationSettings({ userId, userRole, tenantId, users = [] }) {
  const [activeSubTab, setActiveSubTab] = useState('preferences');
  const [preferences, setPreferences] = useState({
    notes: true,
    scores: true,
    coaching: true,
    reviews: true,
    users: true,
    alerts: true,
    tickets: true,
    chat: true,
    announcements: true,
    tasks: true,
    system: true,
    sound_enabled: true,
    email_critical: false,
  });
  const [analytics, setAnalytics] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showAnnouncementDialog, setShowAnnouncementDialog] = useState(false);
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: '',
    message: '',
    priority: 'normal',
    target_roles: [],
    target_user_ids: [],
  });

  // Fetch preferences
  const fetchPreferences = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/notifications/preferences/${userId}`);
      if (res.ok) {
        const data = await res.json();
        setPreferences(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Failed to fetch preferences:', error);
    }
  }, [userId]);

  // Fetch analytics
  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/notifications/analytics?days=30${tenantId ? `&tenant_id=${tenantId}` : ''}`);
      if (res.ok) {
        const data = await res.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  }, [tenantId]);

  // Fetch announcements
  const fetchAnnouncements = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/notifications/announcements${tenantId ? `?tenant_id=${tenantId}` : ''}`);
      if (res.ok) {
        const data = await res.json();
        setAnnouncements(data.announcements || []);
      }
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
    }
  }, [tenantId]);

  // Load all data
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await Promise.all([fetchPreferences(), fetchAnalytics(), fetchAnnouncements()]);
      setIsLoading(false);
    };
    loadData();
  }, [fetchPreferences, fetchAnalytics, fetchAnnouncements]);

  // Save preferences
  const savePreferences = async () => {
    setIsSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/notifications/preferences/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(preferences),
      });
      if (res.ok) {
        toast.success('Notification preferences saved');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setIsSaving(false);
    }
  };

  // Create announcement
  const createAnnouncement = async () => {
    if (!newAnnouncement.title || !newAnnouncement.message) {
      toast.error('Please fill in title and message');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/notifications/announcements?created_by=${userId}&created_by_name=Admin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newAnnouncement,
          target_tenant_id: tenantId,
          target_roles: newAnnouncement.target_roles.length > 0 ? newAnnouncement.target_roles : null,
          target_user_ids: newAnnouncement.target_user_ids.length > 0 ? newAnnouncement.target_user_ids : null,
        }),
      });
      if (res.ok) {
        toast.success('Announcement sent successfully');
        setShowAnnouncementDialog(false);
        setNewAnnouncement({ title: '', message: '', priority: 'normal', target_roles: [], target_user_ids: [] });
        fetchAnnouncements();
      } else {
        throw new Error('Failed to create');
      }
    } catch (error) {
      toast.error('Failed to send announcement');
    }
  };

  // Delete announcement
  const deleteAnnouncement = async (announcementId) => {
    try {
      const res = await fetch(`${API_URL}/api/notifications/announcements/${announcementId}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        toast.success('Announcement deleted');
        fetchAnnouncements();
      }
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  // Prepare chart data
  const dailyChartData = analytics?.daily_breakdown || [];
  const typeChartData = analytics?.by_type ? Object.entries(analytics.by_type).map(([name, value]) => ({ name, value })) : [];
  const priorityChartData = analytics?.by_priority ? Object.entries(analytics.by_priority).map(([name, value]) => ({ name, value })) : [];

  const COLORS = ['#3b82f6', '#f59e0b', '#8b5cf6', '#22c55e', '#ef4444', '#06b6d4', '#ec4899', '#84cc16'];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notification-settings">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-blue-500" />
                Notification Settings
              </CardTitle>
              <CardDescription>
                Manage your notification preferences, view analytics, and send announcements
              </CardDescription>
            </div>
            <Button onClick={() => fetchAnalytics()} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Sub-tabs */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList>
          <TabsTrigger value="preferences" className="gap-2">
            <Bell className="h-4 w-4" />
            Preferences
          </TabsTrigger>
          <TabsTrigger value="announcements" className="gap-2">
            <Megaphone className="h-4 w-4" />
            Announcements
          </TabsTrigger>
          <TabsTrigger value="analytics" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            Analytics
          </TabsTrigger>
        </TabsList>

        {/* Preferences Tab */}
        <TabsContent value="preferences" className="mt-6 space-y-6">
          {/* Sound & Email Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">General Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {preferences.sound_enabled ? (
                    <Volume2 className="h-5 w-5 text-blue-500" />
                  ) : (
                    <VolumeX className="h-5 w-5 text-muted-foreground" />
                  )}
                  <div>
                    <Label>Notification Sound</Label>
                    <p className="text-sm text-muted-foreground">Play sound for new notifications</p>
                  </div>
                </div>
                <Switch
                  checked={preferences.sound_enabled}
                  onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, sound_enabled: checked }))}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <Label>Email for Critical Alerts</Label>
                    <p className="text-sm text-muted-foreground">Send email for urgent and high priority notifications</p>
                  </div>
                </div>
                <Switch
                  checked={preferences.email_critical}
                  onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, email_critical: checked }))}
                />
              </div>
            </CardContent>
          </Card>

          {/* Notification Type Toggles */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Notification Types</CardTitle>
              <CardDescription>Choose which notifications you want to receive</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {notificationTypes.map((type) => {
                  const Icon = type.icon;
                  return (
                    <div
                      key={type.key}
                      className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-muted">
                          <Icon className="h-4 w-4" />
                        </div>
                        <div>
                          <Label className="font-medium">{type.label}</Label>
                          <p className="text-xs text-muted-foreground">{type.description}</p>
                        </div>
                      </div>
                      <Switch
                        checked={preferences[type.key]}
                        onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, [type.key]: checked }))}
                      />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button onClick={savePreferences} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Save Preferences
                </>
              )}
            </Button>
          </div>
        </TabsContent>

        {/* Announcements Tab */}
        <TabsContent value="announcements" className="mt-6 space-y-6">
          {/* Create Announcement */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Broadcast Announcements</CardTitle>
                  <CardDescription>Send announcements to users across the platform</CardDescription>
                </div>
                <Dialog open={showAnnouncementDialog} onOpenChange={setShowAnnouncementDialog}>
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="h-4 w-4 mr-2" />
                      New Announcement
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Create Announcement</DialogTitle>
                      <DialogDescription>
                        This will be sent as a notification to selected users
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Title</Label>
                        <Input
                          placeholder="Announcement title"
                          value={newAnnouncement.title}
                          onChange={(e) => setNewAnnouncement(prev => ({ ...prev, title: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Message</Label>
                        <Textarea
                          placeholder="Write your announcement message..."
                          rows={4}
                          value={newAnnouncement.message}
                          onChange={(e) => setNewAnnouncement(prev => ({ ...prev, message: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Priority</Label>
                        <Select
                          value={newAnnouncement.priority}
                          onValueChange={(value) => setNewAnnouncement(prev => ({ ...prev, priority: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="normal">Normal</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="urgent">Urgent</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Target Audience</Label>
                        <Select
                          value={newAnnouncement.target_roles.join(',') || 'all'}
                          onValueChange={(value) => {
                            if (value === 'all') {
                              setNewAnnouncement(prev => ({ ...prev, target_roles: [] }));
                            } else {
                              setNewAnnouncement(prev => ({ ...prev, target_roles: value.split(',') }));
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select audience" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Users</SelectItem>
                            <SelectItem value="admin">Admins Only</SelectItem>
                            <SelectItem value="auditor">Auditors Only</SelectItem>
                            <SelectItem value="user">Regular Users Only</SelectItem>
                            <SelectItem value="admin,auditor">Admins & Auditors</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowAnnouncementDialog(false)}>
                        Cancel
                      </Button>
                      <Button onClick={createAnnouncement}>
                        <Send className="h-4 w-4 mr-2" />
                        Send Announcement
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {announcements.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Megaphone className="h-12 w-12 mx-auto mb-3 opacity-20" />
                  <p>No announcements yet</p>
                  <p className="text-sm">Create your first announcement to notify users</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Priority</TableHead>
                        <TableHead>Audience</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="w-[80px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {announcements.map((ann) => (
                        <TableRow key={ann.announcement_id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{ann.title}</p>
                              <p className="text-xs text-muted-foreground line-clamp-1">{ann.message}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={priorityColors[ann.priority]}>{ann.priority}</Badge>
                          </TableCell>
                          <TableCell>
                            {ann.target_roles?.length > 0 ? ann.target_roles.join(', ') : 'All users'}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {new Date(ann.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteAnnouncement(ann.announcement_id)}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="mt-6 space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-2">
                  <Bell className="h-5 w-5 text-blue-500" />
                  <p className="text-sm text-muted-foreground">Total (30 days)</p>
                </div>
                <p className="text-2xl font-bold">{analytics?.total_notifications?.toLocaleString() || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-2">
                  <Megaphone className="h-5 w-5 text-pink-500" />
                  <p className="text-sm text-muted-foreground">Announcements</p>
                </div>
                <p className="text-2xl font-bold">{analytics?.announcements_count || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-2">
                  <Eye className="h-5 w-5 text-green-500" />
                  <p className="text-sm text-muted-foreground">Read Rate</p>
                </div>
                <p className="text-2xl font-bold">{analytics?.read_rate_percent || 0}%</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-2">
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                  <p className="text-sm text-muted-foreground">Urgent/High</p>
                </div>
                <p className="text-2xl font-bold">
                  {((analytics?.by_priority?.urgent || 0) + (analytics?.by_priority?.high || 0)).toLocaleString()}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Daily Trend Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Daily Notification Volume</CardTitle>
            </CardHeader>
            <CardContent>
              {dailyChartData.length > 0 ? (
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={dailyChartData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="date" className="text-xs" />
                      <YAxis className="text-xs" />
                      <Tooltip />
                      <Area type="monotone" dataKey="count" name="Notifications" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-20" />
                    <p>No data available</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Type and Priority Distribution */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">By Type</CardTitle>
              </CardHeader>
              <CardContent>
                {typeChartData.length > 0 ? (
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={typeChartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          dataKey="value"
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        >
                          {typeChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                    No data
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">By Priority</CardTitle>
              </CardHeader>
              <CardContent>
                {priorityChartData.length > 0 ? (
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={priorityChartData}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="name" className="text-xs" />
                        <YAxis className="text-xs" />
                        <Tooltip />
                        <Bar dataKey="value" name="Count">
                          {priorityChartData.map((entry, index) => {
                            const colors = { urgent: '#ef4444', high: '#f59e0b', normal: '#3b82f6', low: '#94a3b8' };
                            return <Cell key={`cell-${index}`} fill={colors[entry.name] || '#3b82f6'} />;
                          })}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                    No data
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
